import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Menu,
  X,
  Shield,
  Heart,
  Sparkles,
  BookOpen,
  Users,
  LifeBuoy,
  ChevronDown,
  HeartHandshake,
  LibraryBig,
  Waves,
  Wind,
  Music,
  
  GraduationCap,
  Anchor,
  Moon,
  Compass,
  ShieldCheck,
  Palette,
  Paintbrush,
  Flower2,
  Leaf,
  Search as SearchIcon,
  MessagesSquare,
  UserCircle2,
  HelpCircle,
  ShieldAlert,
  FileText,
  User,
  LogOut,
  NotebookText,
  CalendarDays,
  Eye,
  Brain,
  Lightbulb,
  Layers,
  Sun,
  Home as HomeIcon,
  Camera,
  Receipt,
  Lock,
  type LucideIcon,
  ClipboardList,
  ShoppingBag,
  Gift,
  Shapes,
  Laptop,
  Dices,
  Ghost,
} from "lucide-react";


import { readStoredAuthSession } from "@/lib/auth-session";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ThemeToggle } from "@/components/ThemeToggle";
import { useTherapistOnlyMode, isPreLaunchNow } from "@/lib/launch-mode";
import { useMembership } from "@/hooks/useMembership";
import logoAsset from "@/assets/logo.png.asset.json";
import { LanguageSelector } from "@/components/LanguageSelector";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useHideCommerce } from "@/lib/native-app";
import { secureSignOut } from "@/lib/secure-sign-out";

const logo = logoAsset.url;

type SubLink = { to: string; label: string; icon: LucideIcon; desc?: string; hash?: string; memberOnly?: boolean; /** Optional sub-heading shown above this link in the menu. */ heading?: string };
type NavGroup = {
  key: string;
  label: string;
  icon: LucideIcon;
  to: string;
  hash?: string;
  match: (p: string) => boolean;
  links: SubLink[];
  memberOnly?: boolean;
};

const SHOP_GROUP: NavGroup = {
  key: "shop",
  label: "Shop",
  icon: ShoppingBag,
  to: "/shop",
  match: (p) => p.startsWith("/shop"),
  links: [],
};

const LEGAL_GROUP: NavGroup = {
  key: "legal",
  label: "Legal",
  icon: FileText,
  to: "/terms",
  match: (p) =>
    p.startsWith("/terms") ||
    p.startsWith("/eula") ||
    p.startsWith("/privacy") ||
    p.startsWith("/disclaimer") ||
    p.startsWith("/cookies"),
  links: [
    { to: "/terms", label: "Terms & Conditions", icon: FileText },
    { to: "/eula", label: "End User Licence (EULA)", icon: FileText },
    { to: "/privacy", label: "Privacy Policy", icon: ShieldCheck },
    { to: "/disclaimer", label: "Disclaimer", icon: FileText },
    { to: "/cookies", label: "Cookie Policy", icon: FileText },
  ],
};

const THERAPIST_GROUPS: NavGroup[] = [
  {
    key: "find-therapist",
    label: "Find a Therapist",
    icon: SearchIcon,
    to: "/therapist-directory",
    match: (p) => p.startsWith("/therapist-directory") || p.startsWith("/therapists"),
    links: [],
  },
  {
    key: "become-therapist",
    label: "Become a Therapist Member",
    icon: HeartHandshake,
    to: "/therapist-application",
    match: (p) => p.startsWith("/therapist-application") || p.startsWith("/therapist-terms"),
    links: [],
  },
  {
    key: "about",
    label: "About",
    icon: Heart,
    to: "/about",
    match: (p) => p.startsWith("/about"),
    links: [],
  },
  {
    key: "help-faq",
    label: "Help & FAQ",
    icon: HelpCircle,
    to: "/support",
    match: (p) => p.startsWith("/support") || p.startsWith("/faq") || p.startsWith("/help"),
    links: [
      { to: "/faq", label: "FAQ", icon: HelpCircle },
      { to: "/support", label: "Contact Support", icon: LifeBuoy },
    ],
  },
  LEGAL_GROUP,
];

const LEARN_GROUP: NavGroup = {
  key: "learn",
  label: "Learn",
  icon: BookOpen,
  to: "/learn",
  match: (p) => p.startsWith("/learn"),
  links: [
    { to: "/learn", label: "Learn Hub", icon: BookOpen },
    { to: "/learn/assessments", label: "Self-Assessments", icon: ClipboardList },
    { to: "/learn/assessments/pioneers", label: "Psychology & Trauma Pioneers", icon: Brain },
    { to: "/learn/assessments/trauma-healing-journey", label: "Trauma Healing Journey", icon: Compass },
    { to: "/learn/personality", label: "Personality & Personality Disorders", icon: Shapes },
    { to: "/learn/glossary", label: "Trauma Glossary", icon: LibraryBig },
    { to: "/learn/window-of-tolerance", label: "Window of Tolerance", icon: Waves },
    { to: "/learn/cbt", label: "CBT Toolkit", icon: Brain },
    { to: "/learn/dbt", label: "DBT Skills", icon: Sparkles },
    { to: "/learn/self-harm-support", label: "Self-Harm Support", icon: HeartHandshake },
  ],
};



const FULL_GROUPS: NavGroup[] = [
  {
    key: "understand",
    memberOnly: true,
    label: "Understand",
    icon: BookOpen,
    to: "/library",
    match: (p) =>
      p.startsWith("/library") ||
      p.startsWith("/resources") ||
      p.startsWith("/guide") ||
      p.startsWith("/learn"),
    links: [
      { to: "/library", label: "Recovery Library", icon: BookOpen, desc: "Build knowledge and self-awareness." },
      { to: "/learn", label: "Learn Hub", icon: BookOpen, desc: "Trauma glossary, Window of Tolerance, CBT, DBT & Self-Harm Support." },
      { to: "/learn/men-and-trauma", label: "Men & Trauma", icon: Users, desc: "How trauma and stress can affect men's emotions, bodies and relationships." },
      { to: "/learn/assessments", label: "Self-Assessments", icon: ClipboardList, desc: "Short, non-diagnostic reflections that point you to the right module." },
      { to: "/learn/assessments/pioneers", label: "Psychology & Trauma Pioneers", icon: Brain, desc: "The people, theories, evidence and limitations behind modern trauma thinking." },
      { to: "/learn/assessments/trauma-healing-journey", label: "Trauma Healing Journey", icon: Compass, desc: "An interactive, non-linear guide through safety, regulation and integration." },
      { to: "/learn/personality", label: "Personality & Personality Disorders", icon: Shapes, desc: "Traits, trauma overlap, relationships and treatment without self-diagnosis." },
    ],
  },


  {
    key: "regulate",
    memberOnly: true,
    label: "Regulate",
    icon: Leaf,
    to: "/grounding",
    match: (p) =>
      p.startsWith("/check-in") ||
      p.startsWith("/how-are-you-feeling") ||
      p.startsWith("/trauma-response-wheel") ||
      p.startsWith("/mood") ||
      p.startsWith("/grounding") ||
      p.startsWith("/breathing") ||
      p.startsWith("/tapping") ||
      p.startsWith("/somatic") ||
      p.startsWith("/nervous-system-reset") ||
      p.startsWith("/regulation-compass") ||
      p.startsWith("/sleep") ||
      p.startsWith("/sounds") ||
      p.startsWith("/affirmations") ||
      p.startsWith("/eft-flashcards") ||
      p.startsWith("/healing-journeys") ||
      p.startsWith("/breaking-free") ||
      p.startsWith("/secure-within"),
    links: [
      { to: "/how-are-you-feeling", label: "How Are You Feeling Right Now? — Emotion Wheel", icon: Sparkles, heading: "Check In With Yourself" },
      { to: "/trauma-response-wheel", label: "Trauma Response Wheel", icon: Brain },
      { to: "/regulation-compass", label: "My Regulation Compass", icon: Compass, heading: "Personal Guidance" },
      { to: "/check-in", label: "Daily Check-In", icon: Heart },
      { to: "/grounding", label: "Grounding", icon: Anchor, heading: "Regulation Practices" },
      { to: "/breathing", label: "Breathing", icon: Wind },
      { to: "/tapping", label: "EFT Tapping", icon: Sparkles },
      { to: "/somatic", label: "Somatic Practices", icon: Compass },
      { to: "/sleep", label: "Sleep Support", icon: Moon },
      { to: "/sounds", label: "Calming Sounds", icon: Music },
      { to: "/affirmations", label: "Affirmation Cards", icon: Sparkles },
      { to: "/eft-flashcards", label: "Flashcards", icon: Layers },
      { to: "/healing-journeys", label: "Healing Journeys", icon: Compass, heading: "Healing Journeys" },
      { to: "/breaking-free", label: "Breaking Free — 30-Day Trauma Bond Journey", icon: Heart },
      { to: "/secure-within", label: "Secure Within — 30-Day Attachment Journey", icon: Heart },
      { to: "/nervous-system-reset", label: "Reset & Regulate — 30-Day Nervous System Reset", icon: Leaf },
    ],
  },
  {
    key: "process",
    memberOnly: true,
    label: "Process",
    icon: NotebookText,
    to: "/journals",
    match: (p) =>
      p.startsWith("/journal") ||
      p.startsWith("/dreams") ||
      p.startsWith("/dissociation-journal") ||
      p.startsWith("/abuse-journal") ||
      p.startsWith("/trigger-journal") ||
      p.startsWith("/music-journal") ||
      p.startsWith("/safety-stabilisation") ||
      p.startsWith("/know-your-patterns") ||
      p.startsWith("/shadow-") ||
      p.startsWith("/inner-child") ||
      p.startsWith("/creative-reflection") ||
      p.startsWith("/art-board") ||
      p.startsWith("/guided-journal"),
    links: [
      { to: "/journals", label: "All Journals", icon: Layers },
      { to: "/journal", label: "Recovery Journal", icon: NotebookText },
      { to: "/dreams", label: "Dream Journal", icon: Moon },
      { to: "/trigger-journal", label: "Trigger Journal", icon: Anchor },
      { to: "/music-journal", label: "Music, Memory & Healing Journal", icon: Music },
      { to: "/dissociation-journal", label: "Dissociation & Grounding Journal", icon: Anchor },
      { to: "/abuse-journal", label: "Abuse Recovery & Evidence Journal", icon: ShieldCheck },
      { to: "/shadow-journey", label: "Guided Shadow Journal", icon: Compass },
      { to: "/safety-stabilisation", label: "Safety & Stabilisation", icon: ShieldCheck },
      { to: "/know-your-patterns", label: "Know Your Patterns", icon: Compass },
      { to: "/shadow-work", label: "Shadow Work", icon: Eye },
      { to: "/inner-child", label: "Inner Child Work", icon: Heart },
      { to: "/creative-reflection", label: "Creative Reflection", icon: Palette },
      { to: "/art-board", label: "Drawing Board", icon: Paintbrush },
    ],
  },
  {
    key: "integrate",
    memberOnly: true,
    label: "Integrate",
    icon: Flower2,
    to: "/holistic-wellbeing",
    match: (p) =>
      p.startsWith("/holistic-wellbeing") ||
      p.startsWith("/integrate/") ||
      p.startsWith("/nature-connection") ||
      p.startsWith("/relaxation-practices") ||
      p.startsWith("/aromatherapy") ||
      p.startsWith("/forest-bathing") ||
      p.startsWith("/gardening") ||
      p.startsWith("/acts-of-self-care") ||
      p.startsWith("/self-love"),
    links: [
      { to: "/nature-connection", label: "Nature Connection", icon: Leaf },
      { to: "/relaxation-practices", label: "Relaxation Practices", icon: Flower2 },
      { to: "/aromatherapy", label: "Aromatherapy", icon: Flower2 },
      { to: "/forest-bathing", label: "Forest Bathing", icon: Leaf },
      { to: "/gardening", label: "Gardening", icon: Flower2 },
      { to: "/acts-of-self-care", label: "Acts of Self-Care", icon: Sparkles },
      { to: "/self-love", label: "Self-Love", icon: Heart },
      {
        to: "/integrate/life-worth-living",
        label: "Building a Life Worth Living",
        icon: Sparkles,
      },
      { to: "/integrate/self-leadership", label: "Self-Leadership", icon: Heart },
      { to: "/integrate/your-inner-team", label: "Your Inner Team", icon: Heart },
      {
        to: "/integrate/triggers-as-teachers",
        label: "Triggers as Teachers",
        icon: Sparkles,
      },
      { to: "/integrate/hidden-strengths", label: "Hidden Strengths", icon: Sparkles },
      {
        to: "/integrate/letters-never-sent",
        label: "Letters You'll Never Send",
        icon: Heart,
      },
      {
        to: "/integrate/comfort-collections",
        label: "Comfort Collections",
        icon: Heart,
      },
      { to: "/integrate/finding-awe", label: "Finding Awe", icon: Sparkles },
      { to: "/integrate/the-recovery-home", label: "The Recovery Home", icon: Flower2 },
      { to: "/sanctuary", label: "The Healing Sanctuary", icon: Moon },
      { to: "/integrate/micro-adventures", label: "Micro Adventures", icon: Leaf },
    ],

  },
  {
    key: "community",
    memberOnly: true,
    label: "Community",
    icon: Users,
    to: "/community",
    match: (p) => p.startsWith("/community") || p.startsWith("/grief-loss"),
    links: [
      { to: "/community", label: "Community Feed", icon: MessagesSquare },
      { to: "/grief-loss", label: "Grief & Loss", icon: HeartHandshake },
      { to: "/community-guidelines", label: "Community Guidelines", icon: FileText },
    ],
  },
  {
    key: "find-therapist",
    label: "Find a Therapist",
    icon: HeartHandshake,
    to: "/therapists",
    match: (p) =>
      p.startsWith("/therapists") ||
      p.startsWith("/therapy-approaches") ||
      p.startsWith("/quiz"),
    links: [
      { to: "/therapists", label: "Therapist Directory", icon: SearchIcon },
      { to: "/therapy-approaches", label: "Therapy Approaches", icon: BookOpen },
    ],
  },
  {
    key: "events",
    memberOnly: true,
    label: "Live Events",
    icon: CalendarDays,
    to: "/events",
    match: (p) => p.startsWith("/events") || p.startsWith("/courses") || p.startsWith("/workshops"),
    links: [
      { to: "/events", label: "Upcoming Events", icon: CalendarDays, hash: "upcoming" },
    ],
  },
  {
    key: "for-therapists",
    label: "For Therapists",
    icon: HeartHandshake,
    to: "/therapist-application",
    match: (p) =>
      p.startsWith("/therapist-application") ||
      p.startsWith("/therapist-terms") ||
      p.startsWith("/therapist-directory"),
    links: [
      { to: "/therapist-application", label: "Become a Therapist Member", icon: HeartHandshake },
      { to: "/therapist-terms", label: "Therapist Membership Terms", icon: FileText },
    ],
  },
  {
    key: "blog",
    label: "Blog",
    icon: BookOpen,
    to: "/blog",
    match: (p) => p.startsWith("/blog"),
    links: [],
  },

  {
    key: "help-centre",
    label: "Help Centre",
    icon: HelpCircle,
    to: "/support",
    match: (p) =>
      p.startsWith("/support") ||
      p.startsWith("/help") ||
      p.startsWith("/faq") ||
      p.startsWith("/membership") ||
      p.startsWith("/crisis"),
    links: [
      { to: "/support", label: "Support", icon: LifeBuoy },
      { to: "/faq", label: "Frequently Asked Questions", icon: MessagesSquare },
      { to: "/help", label: "Technical Support", icon: HelpCircle },
      // Membership Information hidden pre-launch (admin-only page).
      { to: "/therapist-application", label: "Therapist Information", icon: HeartHandshake },
      { to: "/crisis", label: "Safety Information", icon: ShieldAlert },
    ],
  },
];

// Primary navigation shown in full only on desktop (lg+).
// Shop is filtered out inside the native iOS app (hideCommerce).
const PRIMARY_NAV: { to: string; label: string }[] = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/membership-benefits", label: "Membership" },
  { to: "/explore", label: "Explore" },
  { to: "/recovery-board", label: "Recovery Board" },
  { to: "/halloween", label: "Halloween Edition" },
  { to: "/shop", label: "Shop" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },

];

export function Header() {

  const headerRef = useRef<HTMLElement | null>(null);
  const [user, setUser] = useState<null | { id: string; email?: string }>(null);
  // False until auth is known. Until then the header shows no sign-in
  // affordance, so a signed-in member never sees "Log in" flash first.
  const [authKnown, setAuthKnown] = useState(false);

  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const therapistOnlyNav = useTherapistOnlyMode();
  // Auth CTAs (Log In / Join) only hide while we are genuinely pre-launch.
  // Signed-out visitors post-launch must always be able to log in or join.
  const therapistOnly = therapistOnlyNav && isPreLaunchNow();
  const membership = useMembership();
  const hideCommerce = useHideCommerce();
  // Admins and signed-in members always see the full app nav, even during
  // therapist-only launch mode. Guests during launch keep the limited menu.
  const MY_MEMBERSHIP_GROUP: NavGroup = {
    key: "my-membership",
    label: "My Membership",
    icon: UserCircle2,
    to: "/my-membership",
    memberOnly: true,
    match: (p) =>
      p.startsWith("/my-membership") ||
      p.startsWith("/profile") ||
      p.startsWith("/favorites") ||
      p.startsWith("/preferences") ||
      p.startsWith("/account/security") ||
      p.startsWith("/account/devices") ||
      p.startsWith("/membership") ||
      p.startsWith("/member-welcome"),
    links: [
      { to: "/member-welcome", label: "Welcome to Recovery Trauma™", icon: Sparkles },
      { to: "/profile", label: "My Profile", icon: UserCircle2 },
      { to: "/favorites", label: "My Favourites", icon: Heart },
      { to: "/my-events", label: "My Events", icon: CalendarDays },
      { to: "/portal", label: "Member Portal", icon: Sparkles },
      { to: "/billing", label: "Membership & Billing", icon: Sparkles },
      
      { to: "/preferences", label: "Settings", icon: Shield },
      { to: "/account/devices", label: "My Devices", icon: Laptop },
    ],
  };
  // Pre-launch flag: locks Log In, Join, and all member-only sections behind
  // the membership modal. Post-launch the waitlist row disappears and Log In
  // + Join unlock to their respective routes.
  const prelaunch = therapistOnly;

  // Public visitors see the four main sections with their FULL submenus — the
  // submenu itself is the preview. Clicking any item routes to /membership.
  const PREVIEW_KEYS = ["understand", "regulate", "process", "integrate"] as const;
  const PREVIEW_GROUPS: NavGroup[] = FULL_GROUPS.filter((g) =>
    (PREVIEW_KEYS as readonly string[]).includes(g.key),
  );

  const COMMUNITY_LINKS: SubLink[] = [
    { to: "/community", label: "Community", icon: MessagesSquare, memberOnly: true },
    { to: "/my-events", label: "Events", icon: CalendarDays, memberOnly: true },
    { to: "/events", label: "Workshops", icon: GraduationCap, hash: "live-workshops", memberOnly: true },
    { to: "/library", label: "Library", icon: BookOpen, memberOnly: true },
  ];

  const GUEST_GROUPS: NavGroup[] = [
    {
      key: "crisis",
      label: "Crisis Support",
      icon: ShieldAlert,
      to: "/crisis",
      match: (p) => p.startsWith("/crisis"),
      links: [],
    },
    {
      key: "membership-public",
      label: "Membership",
      icon: Sparkles,
      to: "/membership-benefits",
      match: (p) => p.startsWith("/membership-benefits") || p.startsWith("/membership"),
      links: [],
    },
    {
      key: "blog-public",
      label: "Blog",
      icon: BookOpen,
      to: "/blog",
      match: (p) => p.startsWith("/blog"),
      links: [],
    },

    // Pre-launch waitlist nav item removed; primary CTA is now Create Account below.

    {
      key: "login",
      label: "Log In",
      icon: User,
      to: "/login",
      match: (p) => p.startsWith("/login") || p.startsWith("/admin-login"),
      links: [],
    },
    {
      key: "join",
      label: "Join",
      icon: HeartHandshake,
      to: "/choose-membership",
      match: (p) =>
        p.startsWith("/choose-membership") ||
        p.startsWith("/signup") ||
        p.startsWith("/register-membership"),
      links: [],
    },
    // Understand / Regulate / Process / Integrate — full submenus, member-gated.
    ...PREVIEW_GROUPS,
    {
      key: "community-group",
      label: "Community",
      icon: Users,
      to: "/community",
      match: (p) => p.startsWith("/community") || p.startsWith("/events") || p.startsWith("/library"),
      links: COMMUNITY_LINKS,
      memberOnly: true,
    },
    {
      key: "find-therapist-public",
      label: "Find a Therapist",
      icon: SearchIcon,
      to: "/therapists",
      match: (p) => p.startsWith("/therapist-directory") || p.startsWith("/therapists"),
      links: [],
    },
    {
      key: "for-therapists",
      label: "For Therapists",
      icon: HeartHandshake,
      to: "/therapist-application",
      match: (p) => p.startsWith("/therapist-application") || p.startsWith("/therapist-terms"),
      links: [],
    },
    {
      key: "about-public",
      label: "About",
      icon: Heart,
      to: "/about",
      match: (p) => p.startsWith("/about"),
      links: [],
    },
    {
      key: "help-faq-public",
      label: "Help & FAQ",
      icon: HelpCircle,
      to: "/support",
      match: (p) => p.startsWith("/support") || p.startsWith("/faq") || p.startsWith("/help"),
      links: [
        { to: "/faq", label: "FAQ", icon: HelpCircle },
        { to: "/support", label: "Contact Support", icon: LifeBuoy },
      ],
    },
    LEGAL_GROUP,
  ];

  const ADMIN_AUTH_GROUPS: NavGroup[] = [
    {
      key: "login",
      label: "Log In",
      icon: User,
      to: "/login",
      match: (p) => p.startsWith("/login") || p.startsWith("/admin-login"),
      links: [],
    },
    {
      key: "join",
      label: "Join",
      icon: HeartHandshake,
      to: "/choose-membership",
      match: (p) =>
        p.startsWith("/choose-membership") ||
        p.startsWith("/signup") ||
        p.startsWith("/register-membership"),

      links: [],
    },
  ];
  const canUseMemberNav = Boolean(isAdmin || membership.isMember);
  const baseGroups = canUseMemberNav ? [...FULL_GROUPS] : [...GUEST_GROUPS];
  const withAdminAuth = isAdmin ? [...baseGroups, ...ADMIN_AUTH_GROUPS] : baseGroups;
  // Inside the iOS app, external checkout is hidden: "Join" becomes a free
  // "Create Account" link to /signup, with plans offered via in-app purchase.
  const NATIVE_JOIN_GROUP: NavGroup = {
    key: "join",
    label: "Create Account",
    icon: HeartHandshake,
    to: "/signup",
    match: (p) => p.startsWith("/signup"),
    links: [],
  };
  const baseWithShop = hideCommerce
    ? withAdminAuth.map((g) => (g.key === "join" ? NATIVE_JOIN_GROUP : g))
    : [...withAdminAuth, SHOP_GROUP];
  const groupsForUser = user && canUseMemberNav ? [MY_MEMBERSHIP_GROUP, ...baseWithShop] : baseWithShop;
  // Signed-in people should never be offered "Log In" again, and members should
  // not be shown "Join" (which leads to payment). Admins keep both for testing.
  const GROUPS = !user || isAdmin
    ? groupsForUser
    : groupsForUser.filter(
        (g) => g.key !== "login" && !(canUseMemberNav && g.key === "join"),
      );


  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    async function loadRole(uid: string) {
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: uid,
        _role: "admin",
      });
      setIsAdmin(!error && !!data);
    }
    function apply(u: { id: string; email?: string } | null) {
      setAuthKnown(true);
      if (u) {
        setUser({ id: u.id, email: u.email || "" });
        setIsAdmin(null);
        loadRole(u.id);
      } else {
        setUser(null);
        setIsAdmin(null);
      }
    }
    // Seed from the stored session, then follow the single shared auth
    // broadcast — no second Supabase subscription here.
    const stored = readStoredAuthSession();
    apply(stored?.user ? { id: stored.user.id, email: stored.user.email || "" } : null);
    const handler = (e: Event) => {
      const session = (e as CustomEvent).detail?.session;
      apply(session?.user ? { id: session.user.id, email: session.user.email || "" } : null);
    };
    window.addEventListener("rt-auth-change", handler);
    return () => {
      window.removeEventListener("rt-auth-change", handler);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenSection(null);
  }, [location.pathname]);

  // Keep --rt-header-h in sync with the real (now responsive) header height so
  // the mobile drawer always starts exactly below the bar on every breakpoint.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = headerRef.current;
    if (!el) return;
    const apply = () =>
      document.documentElement.style.setProperty(
        "--rt-header-h",
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);



  // Lock background scroll while the mobile drawer is open.
  // Uses the iOS-safe position:fixed pattern so Safari doesn't keep scrolling
  // the body behind the overlay.
  // Broadcast mobile drawer state so BottomNav / GroundMeNow can hide
  // and the content area gets the full screen.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("rt-mobile-nav", { detail: { open: mobileOpen } }),
    );
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    if (typeof window === "undefined") return;
    const scrollY = window.scrollY;
    const lockedPath = window.location.pathname;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    // A sticky header can't stick inside a fixed body: if the menu was opened
    // after scrolling, the header (and its Close button) slid off screen.
    // Pin it to the top of the screen while the menu is open.
    const headerEl = headerRef.current;
    const prevHeader = headerEl
      ? { position: headerEl.style.position, top: headerEl.style.top, left: headerEl.style.left, right: headerEl.style.right }
      : null;
    if (headerEl) {
      headerEl.style.position = "fixed";
      headerEl.style.top = "0";
      headerEl.style.left = "0";
      headerEl.style.right = "0";
    }
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      if (headerEl && prevHeader) {
        headerEl.style.position = prevHeader.position;
        headerEl.style.top = prevHeader.top;
        headerEl.style.left = prevHeader.left;
        headerEl.style.right = prevHeader.right;
      }
      // Only put the reader back where they were if they are still on the
      // same page. Tapping a menu link opens a new page, which must start
      // at the top rather than inherit the old position.
      if (window.location.pathname === lockedPath) {
        window.scrollTo(0, scrollY);
      } else {
        window.scrollTo(0, 0);
      }
    };
  }, [mobileOpen]);

  const homeActive = location.pathname === "/";

  async function handleSignOut() {
    await secureSignOut(queryClient);
    navigate({ to: "/", replace: true });
  }

  const AccountMenu = ({ triggerClass, label }: { triggerClass: string; label?: string }) => {
    const canShowMemberLinks = Boolean(isAdmin || membership.isMember);
    const sectionLabel =
      "px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground";
    const itemClass =
      "flex min-h-[44px] items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors hover:bg-gold-light/30 hover:text-[var(--gold-deep)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)]/50 data-[status=active]:bg-gold-light/50 data-[status=active]:text-[var(--gold-deep)] data-[status=active]:font-semibold";

    const iconClass = "h-4 w-4 shrink-0 text-[var(--gold-deep)]";
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Account menu" className={triggerClass}>
            <UserCircle2 className="h-4 w-4 shrink-0" strokeWidth={2.25} />
            {label ? <span>{label}</span> : null}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-72 max-h-[80vh] overflow-y-auto overscroll-contain p-1.5"
        >
          {user?.email && (
            <DropdownMenuLabel className="flex items-center gap-2 rounded-md bg-gold-light/25 px-2 py-2">
              <UserCircle2 className="h-5 w-5 shrink-0 text-[var(--gold-deep)]" strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-foreground">Signed in</div>
                <div className="truncate text-[11px] font-normal text-muted-foreground">{user.email}</div>
              </div>
            </DropdownMenuLabel>
          )}

          <div className={sectionLabel}>My Account</div>
          <DropdownMenuItem asChild>
            <Link to="/dashboard" className={`${itemClass} font-semibold`}>
              <HomeIcon className={iconClass} strokeWidth={1.75} />
              My Dashboard
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/my-membership" className={itemClass}>
              <UserCircle2 className={iconClass} strokeWidth={1.5} />
              My Membership
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/profile" className={itemClass}>
              <User className={iconClass} strokeWidth={1.5} />
              View Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/profile" hash="personal-info" className={itemClass}>
              <NotebookText className={iconClass} strokeWidth={1.5} />
              Edit Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/profile" hash="profile-photo" className={itemClass}>
              <Camera className={iconClass} strokeWidth={1.5} />
              Change Profile Photo
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/portal" className={itemClass}>
              <Sparkles className={iconClass} strokeWidth={1.5} />
              Member Portal
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/billing" className={itemClass}>
              <Sparkles className={iconClass} strokeWidth={1.5} />
              Membership &amp; Billing
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/payment-history" className={itemClass}>
              <Receipt className={iconClass} strokeWidth={1.5} />
              Payment History
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/preferences" className={itemClass}>
              <Shield className={iconClass} strokeWidth={1.5} />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/account/devices" className={itemClass}>
              <Laptop className={iconClass} strokeWidth={1.5} />
              My Devices
            </Link>
          </DropdownMenuItem>

          {canShowMemberLinks && (
            <>
              <DropdownMenuSeparator />
              <div className={sectionLabel}>My Wellbeing Journey</div>
              <DropdownMenuItem asChild>
                <Link to="/recovery-board" className={itemClass}>
                  <Dices className={iconClass} strokeWidth={1.5} />
                  Recovery Board
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/halloween" className={itemClass}>
                  <Ghost className={iconClass} strokeWidth={1.5} />
                  Halloween Edition
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/journal" className={itemClass}>
                  <NotebookText className={iconClass} strokeWidth={1.5} />
                  Recovery Journal
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dreams" className={itemClass}>
                  <Moon className={iconClass} strokeWidth={1.5} />
                  Dream Journal
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/shadow-work" className={itemClass}>
                  <Eye className={iconClass} strokeWidth={1.5} />
                  Shadow Work
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/favorites" className={itemClass}>
                  <Heart className={iconClass} strokeWidth={1.5} />
                  My Favourites
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <div className={sectionLabel}>Community &amp; Events</div>
              <DropdownMenuItem asChild>
                <Link to="/community" className={itemClass}>
                  <MessagesSquare className={iconClass} strokeWidth={1.5} />
                  Community Feed
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/events" className={itemClass}>
                  <CalendarDays className={iconClass} strokeWidth={1.5} />
                  Upcoming Events
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/events" hash="recordings" className={itemClass}>
                  <Music className={iconClass} strokeWidth={1.5} />
                  Event Recordings
                </Link>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <div className={sectionLabel}>Understand</div>
          <DropdownMenuItem asChild>
            <Link to="/learn" activeOptions={{ exact: true }} className={itemClass}>
              <BookOpen className={iconClass} strokeWidth={1.5} />
              Learn Hub
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/men-and-trauma" className={itemClass}>
              <Users className={iconClass} strokeWidth={1.5} />
              Men &amp; Trauma
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/assessments/pioneers" className={itemClass}>
              <Brain className={iconClass} strokeWidth={1.5} />
              Psychology & Trauma Pioneers
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/assessments/trauma-healing-journey" className={itemClass}>
              <Compass className={iconClass} strokeWidth={1.5} />
              Trauma Healing Journey
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/personality" className={itemClass}>
              <Shapes className={iconClass} strokeWidth={1.5} />
              Personality &amp; Personality Disorders
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/glossary" className={itemClass}>
              <LibraryBig className={iconClass} strokeWidth={1.5} />
              Trauma Glossary
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/window-of-tolerance" className={itemClass}>
              <Waves className={iconClass} strokeWidth={1.5} />
              Window of Tolerance
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/cbt" className={itemClass}>
              <Brain className={iconClass} strokeWidth={1.5} />
              CBT Toolkit
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/dbt" className={itemClass}>
              <Sparkles className={iconClass} strokeWidth={1.5} />
              DBT Skills
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/learn/self-harm-support" className={itemClass}>
              <HeartHandshake className={iconClass} strokeWidth={1.5} />
              Self-Harm Support
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <div className={sectionLabel}>Breathing</div>
          <DropdownMenuItem asChild>
            <Link to="/breathing" activeOptions={{ exact: true }} className={itemClass}>
              <Wind className={iconClass} strokeWidth={1.5} />
              Breathing
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <div className={sectionLabel}>Therapists</div>
          <DropdownMenuItem asChild>
            <Link to="/therapists" className={itemClass}>
              <SearchIcon className={iconClass} strokeWidth={1.5} />
              Find a Therapist
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/therapy-approaches" className={itemClass}>
              <BookOpen className={iconClass} strokeWidth={1.5} />
              Therapy Approaches
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <div className={sectionLabel}>Support</div>
          <DropdownMenuItem asChild>
            <Link to="/support" className={itemClass}>
              <LifeBuoy className={iconClass} strokeWidth={1.5} />
              Help &amp; Support
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/faq" className={itemClass}>
              <HelpCircle className={iconClass} strokeWidth={1.5} />
              FAQ
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/crisis" className={itemClass}>
              <ShieldAlert className={iconClass} strokeWidth={1.5} />
              Crisis Support
            </Link>
          </DropdownMenuItem>

          {user && isAdmin && (
            <>
              <DropdownMenuSeparator />
              <div className={sectionLabel}>Admin</div>
              <DropdownMenuItem asChild>
                <Link to="/admin" className={itemClass}>
                  <Shield className={iconClass} strokeWidth={1.5} />
                  Admin Dashboard
                </Link>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={handleSignOut}
            className={`${itemClass} text-destructive focus:text-destructive`}
          >
            <LogOut className={iconClass} strokeWidth={1.5} />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };


  return (
    <>
    <header
      ref={headerRef}
      className="sticky top-0 z-50 w-full max-w-full overflow-x-clip border-b border-gold/15 bg-background pt-[env(safe-area-inset-top)]"
    >

      <div className="mx-auto flex min-w-0 max-w-[1320px] items-center gap-2 px-3 py-2 sm:px-6 md:gap-4 md:py-2.5 lg:px-8 lg:py-3">
        {/* Brand */}
        <Link
          to="/"
          aria-label="Recovery Trauma™ — home"
          className="group flex min-w-0 flex-1 items-center gap-2 lg:flex-none"
        >
          <img loading="lazy" decoding="async"
            src={logo}
            alt="Recovery Trauma™"
            className="h-11 w-11 shrink-0 rounded-full object-cover ring-1 ring-gold/50 transition-transform group-hover:scale-105 md:h-12 md:w-12 lg:h-14 lg:w-14"
          />
          <span
            data-no-translate
            className="hidden min-w-0 truncate font-display text-[15px] font-medium tracking-tight text-foreground min-[430px]:inline md:text-base lg:text-lg"
          >
            Recovery Trauma<span className="text-[var(--gold-deep)]">™</span>
          </span>
        </Link>

        {/* Desktop primary navigation (lg+) */}
        <nav className="hidden flex-1 items-center justify-end gap-1 xl:flex xl:pe-1" aria-label="Main">
          {PRIMARY_NAV.map((item) => {
            if (item.to === "/recovery-board" && !(membership.isMember || membership.isAdmin)) return null;
            if (item.to === "/halloween" && !(membership.isMember || membership.isAdmin)) return null;
            if (item.to === "/shop" && hideCommerce) return null;
            return (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={item.to === "/" ? { exact: true } : undefined}
                className="rounded-full px-3 py-2 text-sm font-medium tracking-tight text-foreground/80 transition-colors hover:bg-gold-light/30 hover:text-[var(--gold-deep)] data-[status=active]:bg-gold-light/45 data-[status=active]:text-[var(--gold-deep)]"
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="flex shrink-0 items-center gap-1 min-[400px]:gap-1.5 md:gap-2">

          {/* Day / Night mode — visible on mobile too */}
          <ThemeToggle triggerClassName="inline-flex h-9 w-9 min-[440px]:h-10 min-[440px]:w-10 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-gold-light/30 text-[var(--gold-deep)] transition-colors hover:bg-gold/10" />

          {/* Language / automatic translation — visible on mobile too */}
          <LanguageSelector className="inline-flex min-h-[40px] shrink-0 items-center gap-1 rounded-full border border-gold/40 bg-gold-light/30 px-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--gold-deep)] hover:bg-gold/10 md:gap-1.5 md:px-3 md:text-xs" />

          {/* Shop — visible at every width (hidden inside the iOS app) */}
          {!therapistOnly && !hideCommerce && (
            <Link
              to="/shop"
              aria-label="Shop — Gift Memberships"
              className="hidden h-9 min-w-9 shrink-0 items-center justify-center gap-1.5 rounded-full min-[360px]:inline-flex border border-gold/40 bg-gold-light/30 px-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--gold-deep)] transition-colors hover:bg-gold/10 min-[440px]:h-10 md:px-3 md:text-xs xl:hidden"
            >
              <Gift className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
              <span className="hidden md:inline">Shop</span>
            </Link>
          )}

          {/* Install the app — the browser only offers this where installing
              works (Chrome/Edge). It stays hidden everywhere else. */}
          <InstallAppButton
            title="Install the Recovery Trauma™ app on this computer"
            label="Install"
            iconClassName="h-3.5 w-3.5 shrink-0"
            className="hidden min-h-[40px] shrink-0 items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/30 px-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--gold-deep)] transition-colors hover:bg-gold/10 md:inline-flex md:px-3 md:text-xs"
          />

          {/* Tablet-only shortcuts */}
          {!therapistOnly && (
            <>
              <Link
                to="/membership-benefits"
                className="hidden min-h-[40px] items-center rounded-full px-3 text-sm font-medium text-foreground/85 transition-colors hover:bg-gold-light/30 hover:text-[var(--gold-deep)] md:inline-flex xl:hidden"
              >
                Membership
              </Link>
              <Link
                to="/blog"
                className="hidden min-h-[40px] items-center rounded-full px-3 text-sm font-medium text-foreground/85 transition-colors hover:bg-gold-light/30 hover:text-[var(--gold-deep)] md:inline-flex xl:hidden"
              >
                Blog
              </Link>
            </>
          )}


          {user ? (
            <AccountMenu
              label="MY ACCOUNT"
              triggerClass="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/30 px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--gold-deep)] md:px-3 md:text-xs"
            />
          ) : !therapistOnly && authKnown ? (
            <Link
              to="/login"
              className="inline-flex min-h-[40px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-gold/40 bg-gold-light/30 px-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--gold-deep)] md:px-3 md:text-xs md:tracking-[0.08em]"
            >
              <User className="hidden h-3.5 w-3.5 min-[420px]:block" strokeWidth={2.25} aria-hidden />
              Log in
            </Link>
          ) : null}

          {/* Gold CTA — mobile, tablet and desktop */}
          {!therapistOnly && authKnown && (
            <Link
              to={user ? "/dashboard" : hideCommerce ? "/signup" : "/choose-membership"}
               // Phones: the header row has no room for this pill next to
               // theme, language, Log in and Menu — it pushed the whole page
               // wider than the screen. On phones it lives at the top of the menu.
               className={`${user ? "hidden md:inline-flex" : "inline-flex"} min-h-[40px] shrink-0 items-center whitespace-nowrap rounded-full border border-[var(--gold-deep)]/60 bg-[image:var(--gradient-gold)] px-3 text-[11px] font-bold uppercase tracking-[0.06em] text-foreground shadow-[var(--shadow-gold)] md:px-4 md:text-xs md:tracking-[0.08em] lg:min-h-[46px] lg:px-6`}
            >
              {user ? "My Dashboard" : "Join"}
            </Link>
          )}


          <button
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/30 px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--gold-deep)] hover:bg-gold/10 md:px-3 md:text-xs"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="rt-mobile-nav"
          >
            <span>{mobileOpen ? "Close" : "Menu"}</span>
            {mobileOpen ? <X className="h-4 w-4 shrink-0" /> : <Menu className="h-4 w-4 shrink-0" />}

          </button>

        </div>
      </div>

    </header>





      {/* Mobile drawer — fixed overlay so background page can't scroll */}
      {mobileOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 top-[var(--rt-header-h,64px)] z-40 bg-foreground/20 backdrop-blur-[2px]"
          />
          <div
            ref={(el) => {
              if (el) el.scrollTop = 0;
            }}
            id="rt-mobile-nav"
            className="fixed inset-x-0 top-[var(--rt-header-h,64px)] z-50 max-h-[calc(100dvh-var(--rt-header-h,64px))] overflow-y-auto overscroll-contain rounded-b-2xl border-t border-gold/15 bg-background px-4 pt-2 shadow-lg"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
              WebkitOverflowScrolling: "touch",
            }}

          >

            <nav className="mx-auto flex min-w-0 max-w-6xl flex-col gap-0.5">
            {!user && authKnown && !therapistOnly && (
              <Link
                to="/signup"
                onClick={() => setMobileOpen(false)}
                className="mb-2 mt-1 flex min-h-[48px] items-center justify-center rounded-full bg-[var(--gradient-gold)] px-4 py-2.5 text-sm font-semibold tracking-wide text-foreground shadow-[var(--shadow-gold)]"
              >
                Create Account
              </Link>
            )}
            <div className="mb-1 flex items-center justify-between gap-2 rounded-lg border border-gold/25 bg-gold-light/15 px-3 py-2 md:hidden">
              <span className="text-[13px] font-medium text-foreground/80">Language</span>
              <LanguageSelector variant="full" />
            </div>
            <InstallAppButton
              label="Install the Recovery Trauma™ app"
              iconClassName="h-[18px] w-[18px] shrink-0"
              className="mb-1 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-gold/25 bg-gold-light/15 px-3 text-[15px] font-medium tracking-tight text-[var(--gold-deep)] transition-colors hover:bg-gold-light/30"
            />
            <Link
              to="/"
              activeOptions={{ exact: true }}
              onClick={() => setMobileOpen(false)}
              className={`flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium tracking-tight transition-colors ${
                homeActive
                  ? "bg-gold-light/40 text-[var(--gold-deep)]"
                  : "text-foreground/85 hover:bg-gold-light/30"
              }`}
            >
              <HomeIcon className="h-[18px] w-[18px] shrink-0 text-[var(--gold-deep)]" strokeWidth={1.5} aria-hidden />
              <span className="truncate">Home</span>
            </Link>

            {/* Priority quick links — the shortest path to what people need. */}
            {(user
              ? ([
                  { to: "/dashboard", label: "My Dashboard", icon: HomeIcon },
                  { to: "/recovery-board", label: "Recovery Board", icon: Dices },
                  { to: "/sanctuary", label: "The Healing Sanctuary", icon: Moon },
                  { to: "/halloween", label: "Halloween Edition", icon: Ghost },
                  { to: "/my-membership", label: "My Membership", icon: UserCircle2 },
                  { to: "/portal", label: "Member Portal", icon: Sparkles },
                  { to: "/shop", label: "Shop", icon: ShoppingBag },
                  { to: "/journals", label: "Journals", icon: NotebookText },
                  { to: "/grounding", label: "Exercises & Tools", icon: Leaf },
                  { to: "/events", label: "Live Workshops", icon: CalendarDays },
                  { to: "/profile", label: "Account", icon: User },
                ] as const)
              : ([
                  { to: "/about", label: "About", icon: Heart },
                  { to: "/membership-benefits", label: "Membership", icon: Sparkles },
                  { to: "/explore", label: "Explore & Resources", icon: Compass },
                  { to: "/shop", label: "Shop", icon: ShoppingBag },
                  { to: "/contact", label: "Contact", icon: LifeBuoy },
                ] as const)
            ).map((item) => {
              if (item.to === "/recovery-board" && !(membership.isMember || membership.isAdmin)) return null;
              if (item.to === "/halloween" && !(membership.isMember || membership.isAdmin)) return null;
              if (item.to === "/shop" && hideCommerce) return null;
              const ItemIcon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium tracking-tight text-foreground/85 transition-colors hover:bg-gold-light/30 data-[status=active]:bg-gold-light/40 data-[status=active]:text-[var(--gold-deep)]"
                >
                  <ItemIcon className="h-[18px] w-[18px] shrink-0 text-[var(--gold-deep)]" strokeWidth={1.5} aria-hidden />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}


            {user && (
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  handleSignOut();
                }}
                className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-start text-[15px] font-medium tracking-tight text-destructive hover:bg-destructive/10"
              >
                <LogOut className="h-[18px] w-[18px] shrink-0 text-[var(--gold-deep)]" strokeWidth={1.5} aria-hidden />
                Log Out
              </button>
            )}

            <div className="mt-3 mb-1 flex items-center justify-between gap-3 px-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Explore everything
              </span>
              <span className="md:hidden">
                <ThemeToggle triggerClassName="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full border border-gold/40 bg-gold-light/30 text-[var(--gold-deep)]" />
              </span>
            </div>


            {GROUPS.map((group) => {
              const active = group.match(location.pathname);
              const isOpen = openSection === group.key;
              const hasLinks = group.links.length > 0;
              const GroupIcon = group.icon;
              const isGuest = !canUseMemberNav;
              const lockForGuest = isGuest && !!group.memberOnly;
              const showLock = lockForGuest;

              const rowClass = `flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium tracking-tight transition-colors ${
                active
                  ? "bg-gold-light/40 text-[var(--gold-deep)]"
                  : "text-foreground/85 hover:bg-gold-light/30"
              }`;

              const labelInner = (
                <>
                  <GroupIcon className="h-[18px] w-[18px] shrink-0 text-[var(--gold-deep)]" strokeWidth={1.5} aria-hidden />
                  <span className="truncate">{group.label}</span>
                  {showLock && (
                    <Lock
                      className="ms-auto h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)]/70"
                      strokeWidth={1.5}
                      aria-label="Members only"
                    />
                  )}
                </>
              );

              if (!hasLinks) {
                if (lockForGuest) {
                  return (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => {
                        setMobileOpen(false);
                        navigate({ to: "/membership" });
                      }}
                      className={`${rowClass} text-start w-full`}
                    >
                      {labelInner}
                    </button>
                  );
                }
                return (
                  <Link
                    key={group.key}
                    to={group.to}
                    hash={group.hash}
                    onClick={() => setMobileOpen(false)}
                    className={rowClass}
                  >
                    {labelInner}
                  </Link>
                );
              }

              return (
                <Collapsible
                  key={group.key}
                  open={isOpen}
                  onOpenChange={(o) => setOpenSection(o ? group.key : null)}
                  id={`nav-section-${group.key}`}
                >
                  <CollapsibleTrigger
                    aria-label={isOpen ? `Collapse ${group.label}` : `Expand ${group.label}`}
                    className={`w-full text-start ${rowClass}`}
                  >
                    {labelInner}
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-[var(--gold-deep)] transition-transform duration-300 ${
                        showLock ? "ms-2" : "ms-auto"
                      } ${isOpen ? "rotate-180" : ""}`}
                      strokeWidth={1.5}
                      aria-hidden
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                    <ul className="ms-5 my-1 border-s-2 border-gold/25 ps-3 pe-1">
                      {(group.links[0]?.to === group.to
                        ? group.links
                        : [{ to: group.to, hash: group.hash, label: `Explore ${group.label}`, icon: group.icon } as SubLink, ...group.links]
                      ).map((link) => {
                        const LinkIcon = link.icon;
                        const isFav = link.to === "/favorites";
                        const iconClass = "h-[16px] w-[16px] shrink-0 text-[var(--gold-deep)]";
                        const iconFill = undefined;
                        const linkLock = isGuest && (group.memberOnly || link.memberOnly);
                        const headingEl = link.heading ? (
                          <p className="mt-2 mb-0.5 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]/80">
                            {link.heading}
                          </p>
                        ) : null;
                        if (linkLock) {
                          return (
                            <li key={`${link.to}#${link.hash ?? ""}-${link.label}`}>
                              {headingEl}
                              <button
                                type="button"
                                onClick={() => {
                                  setMobileOpen(false);
                                  navigate({ to: "/membership" });
                                }}
                                className="flex min-h-[44px] w-full items-center gap-3 rounded-md px-2 py-2 text-start text-sm text-foreground/75 transition-colors hover:bg-gold-light/30 hover:text-[var(--gold-deep)]"
                              >
                                <LinkIcon className={iconClass} strokeWidth={1.5} fill="none" aria-hidden />
                                <span className="truncate">{link.label}</span>
                                <Lock
                                  className="ms-auto h-3 w-3 shrink-0 text-[var(--gold-deep)]/60"
                                  strokeWidth={1.5}
                                  aria-label="Members only"
                                />
                              </button>
                            </li>
                          );
                        }
                        return (
                          <li key={`${link.to}#${link.hash ?? ""}-${link.label}`}>
                            {headingEl}
                            <Link
                              to={link.to}
                              hash={link.hash}
                              activeOptions={{ exact: link.to === "/learn" || link.to === "/library" }}
                              onClick={() => setMobileOpen(false)}
                              className="flex min-h-[44px] w-full items-center gap-3 rounded-md px-2 py-2 text-sm text-foreground/75 transition-colors hover:bg-gold-light/30 hover:text-[var(--gold-deep)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)]/50 data-[status=active]:bg-gold-light/60 data-[status=active]:text-[var(--gold-deep)] data-[status=active]:font-semibold"
                            >
                              <LinkIcon className={iconClass} strokeWidth={1.5} fill="none" aria-hidden />
                              <span className="truncate">{link.label}</span>
                            </Link>

                          </li>
                        );
                      })}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}




            {!user && authKnown && (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="mt-4 flex min-h-[48px] items-center justify-center rounded-full bg-[var(--gradient-gold)] px-4 py-2.5 text-sm font-semibold tracking-wide text-foreground shadow-[var(--shadow-gold)]"
              >
                Log in
              </Link>
            )}

            {user && (
              <>
                <div className="mt-5 mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  My Membership
                </div>
                <Link
                  to="/profile"
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium tracking-tight text-foreground/85 hover:bg-gold-light/30"
                >
                  <User className="h-[18px] w-[18px] shrink-0 text-[var(--gold-deep)]" strokeWidth={1.5} aria-hidden />
                  Profile
                </Link>
                <Link
                  to="/membership"
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium tracking-tight text-foreground/85 hover:bg-gold-light/30"
                >
                  <Sparkles className="h-[18px] w-[18px] shrink-0 text-[var(--gold-deep)]" strokeWidth={1.5} aria-hidden />
                  Membership
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className="mt-3 flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-4 py-2.5 text-sm font-semibold tracking-wide text-foreground shadow-[var(--shadow-gold)]"
                  >
                    <Shield className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                    Admin Dashboard
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    handleSignOut();
                  }}
                  className="mt-1 flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-start text-[15px] font-medium tracking-tight text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="h-[18px] w-[18px] shrink-0 text-[var(--gold-deep)]" strokeWidth={1.5} aria-hidden />
                  Logout
                </button>
              </>
            )}




            {user && isAdmin && (
              <>
                <Link
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  className="mt-3 flex min-h-[48px] items-center justify-center rounded-full bg-[var(--gradient-gold)] px-4 py-2.5 text-sm font-semibold tracking-wide text-foreground shadow-[var(--shadow-gold)]"
                >
                  Admin Dashboard
                </Link>

                {/* Removed: the flat "Admin · Platform Testing" list. The
                    Admin Dashboard button above opens the structured
                    dashboard at /admin, which already groups every section
                    (Overview, Launch, Members, Therapists, Content,
                    Community & Safety, Business, Settings) in its own
                    sidebar. Duplicating them here just created a huge
                    mobile scroll wall. */}

              </>
            )}
            {!isAdmin && !therapistOnly && (
              <Link
                to="/admin-login"
                onClick={() => setMobileOpen(false)}
                className="mt-4 flex min-h-[44px] items-center justify-center rounded-full border border-gold/40 px-4 py-2 text-sm font-medium tracking-wide text-[var(--gold-deep)] hover:bg-gold-light/30"
              >
                Admin Login
              </Link>
            )}
            </nav>
          </div>
        </>
      )}

      {/*
        Radix Dialog already handles focus trap, Esc-to-close, focus return
        to the trigger, `aria-modal`, and labelling via DialogTitle /
        DialogDescription. We add explicit IDs + autofocus on the primary
        CTA so screen-reader and keyboard users land on "Become a Founding
        Member" immediately and can complete the flow with Tab + Enter only.
      */}
      <Dialog open={memberModalOpen} onOpenChange={setMemberModalOpen}>
        <DialogContent
          className="max-w-md border-gold/30 bg-background"
          aria-labelledby="membership-modal-title"
          aria-describedby="membership-modal-description"
        >
          <DialogHeader>
            <DialogTitle
              id="membership-modal-title"
              className="font-display text-xl tracking-tight"
            >
              Members only
            </DialogTitle>
            <DialogDescription
              id="membership-modal-description"
              className="pt-2 text-sm leading-relaxed text-foreground/75"
            >
              This section is part of Recovery Trauma<span className="text-[var(--gold-deep)]">™</span> membership.
              <br />
              <br />
              Join to unlock every recovery tool, live workshop and the member community.

            </DialogDescription>
          </DialogHeader>
          <DialogFooter
            className="flex flex-col gap-2 sm:flex-col"
            role="group"
            aria-label="Membership actions"
          >
            <Link
              to={hideCommerce ? (user ? "/app-membership" : "/signup") : "/choose-membership"}
              autoFocus
              aria-label="Join Recovery Trauma"
              onClick={() => setMemberModalOpen(false)}
              className="flex min-h-[48px] items-center justify-center rounded-full bg-[var(--gradient-gold)] px-4 py-2.5 text-sm font-semibold tracking-wide text-foreground shadow-[var(--shadow-gold)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2"
            >
              {hideCommerce ? (user ? "View Plans" : "Create Account") : "Join Recovery Trauma™"}
            </Link>
            <Link
              to="/explore"
              onClick={() => setMemberModalOpen(false)}
              className="flex min-h-[44px] items-center justify-center rounded-full border border-gold/40 px-4 py-2 text-sm font-medium tracking-wide text-[var(--gold-deep)] hover:bg-gold-light/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2"
            >
              Learn More
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>

  );
}
